import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Copy, RefreshCw, KeyRound, Eye, EyeOff } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Muestra las credenciales de acceso de una modelo (username + password).
 * Permite generar/regenerar la contraseña. Solo admin/asistente pueden verlo.
 */
export default function ModelCredentialsSection({ modelId, modelName }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  const load = async () => {
    if (!modelId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/models/${modelId}/credentials`, { withCredentials: true });
      setData(r.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [modelId]);

  const regenerate = async () => {
    if (!window.confirm(`¿Regenerar contraseña para ${modelName || "esta modelo"}? La anterior dejará de funcionar.`)) return;
    setLoading(true);
    try {
      const r = await axios.post(`${API}/models/${modelId}/credentials`, {}, { withCredentials: true });
      setData({
        has_credentials: true,
        modelo_name: r.data.modelo_name,
        username: r.data.username,
        password_hint: r.data.password,
        password_updated_at: new Date().toISOString(),
      });
      setShowPass(true);
      toast.success("Credenciales actualizadas");
    } catch {
      toast.error("Error al regenerar credenciales");
    } finally {
      setLoading(false);
    }
  };

  const copy = async (val, label) => {
    try {
      await navigator.clipboard.writeText(val);
      toast.success(`${label} copiado`);
    } catch {
      toast.error("No se pudo copiar");
    }
  };

  return (
    <div
      data-testid="model-credentials-section"
      className="p-3 bg-blue-500/5 border border-blue-500/25 rounded-lg space-y-2"
    >
      <div className="flex items-center gap-2 text-blue-300 text-sm font-semibold">
        <KeyRound className="w-4 h-4" />
        Credenciales de acceso
      </div>

      {loading && <div className="text-xs text-gray-500">Cargando…</div>}

      {!loading && (!data || !data.has_credentials) && (
        <div className="space-y-2">
          <p className="text-xs text-gray-400">
            Esta modelo aún no tiene credenciales para acceder al CRM.
          </p>
          <Button
            data-testid="generate-model-credentials-btn"
            size="sm"
            onClick={regenerate}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <KeyRound className="w-3.5 h-3.5 mr-1.5" />
            Generar credenciales
          </Button>
        </div>
      )}

      {!loading && data && data.has_credentials && (
        <>
          <div className="grid grid-cols-1 gap-2">
            <div className="flex items-center gap-2 bg-zinc-900/60 rounded p-2">
              <span className="text-[10px] text-gray-500 uppercase w-16 shrink-0">Usuario</span>
              <span
                data-testid="model-username-display"
                className="text-white text-sm font-mono flex-1 truncate"
              >
                {data.username || "—"}
              </span>
              <button
                type="button"
                onClick={() => copy(data.username, "Usuario")}
                className="text-gray-400 hover:text-white p-1"
                title="Copiar usuario"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="flex items-center gap-2 bg-zinc-900/60 rounded p-2">
              <span className="text-[10px] text-gray-500 uppercase w-16 shrink-0">Password</span>
              <span
                data-testid="model-password-display"
                className="text-white text-sm font-mono flex-1 truncate"
              >
                {data.password_hint
                  ? showPass
                    ? data.password_hint
                    : "•".repeat(Math.min(data.password_hint.length, 10))
                  : "(oculta — regenera para verla)"}
              </span>
              {data.password_hint && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="text-gray-400 hover:text-white p-1"
                    title={showPass ? "Ocultar" : "Mostrar"}
                  >
                    {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  <button
                    type="button"
                    onClick={() => copy(data.password_hint, "Contraseña")}
                    className="text-gray-400 hover:text-white p-1"
                    title="Copiar contraseña"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            {data.password_updated_at && (
              <span className="text-[10px] text-gray-500">
                Actualizada: {new Date(data.password_updated_at).toLocaleString("es")}
              </span>
            )}
            <Button
              data-testid="regenerate-model-credentials-btn"
              size="sm"
              variant="ghost"
              onClick={regenerate}
              className="text-blue-400 hover:text-blue-300 ml-auto"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
              Regenerar
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
